import {useMemo} from "react";
import {AnankeSkeleton} from "@vesta/ananke/components/anankeSkeleton";
import {AnankeText} from "@vesta/ananke/components/anankeText";
import {TableErrorComponent} from "~/components/ottopilotDataViewer/tableErrorComponent";
import {
    ActivityTimelineDaySection,
} from "~/modules/activityTimeline/cards/ActivityLogCardShell";
import {useActivityTimelineData} from "~/modules/activityTimeline/hooks/useActivityTimelineData";
import {limitTimelineDayGroups} from "~/modules/activityTimeline/timeline/timelineGenerator";
import type {EventContainerWithEvents} from "~/utilities/apis/api";

function ActivityTimelineLoadingSkeleton() {
    return (
        <div className="grid w-full min-w-0 gap-ds-space-150 px-ds-space-200 py-ds-space-150">
            <AnankeSkeleton width="28%" height={14} />
            <AnankeSkeleton width="100%" height={52} borderRadius="4px" />
            <AnankeSkeleton width="100%" height={52} borderRadius="4px" />
            <AnankeSkeleton width="100%" height={52} borderRadius="4px" />
        </div>
    );
}

export function ActivityTimeline({
    eventContainer,
    maxItems,
}: {
    eventContainer: EventContainerWithEvents;
    /** When set, only the most recent N activity cards are shown (newest first). */
    maxItems?: number;
}) {
    const {groups, status, error, isLoading, isFetching} = useActivityTimelineData(eventContainer);

    const displayGroups = useMemo(() => {
        if (maxItems === undefined) {
            return groups;
        }
        return limitTimelineDayGroups(groups, maxItems);
    }, [groups, maxItems]);

    const showLoading = isLoading === true || isFetching === true;

    if (showLoading === true) {
        return <ActivityTimelineLoadingSkeleton />;
    }

    if (status === "error") {
        return (
            <div className="w-full min-w-0 px-ds-space-200 py-ds-space-150">
                <TableErrorComponent error={error?.message ?? "Failed to load activity"} />
            </div>
        );
    }

    if (displayGroups.length === 0) {
        return (
            <div className="w-full min-w-0 px-ds-space-200 py-ds-space-150">
                <AnankeText color="color.text.subtlest">No activity yet.</AnankeText>
            </div>
        );
    }

    return (
        <div className="grid w-full min-w-0 gap-ds-space-200 px-ds-space-200 py-ds-space-150">
            {displayGroups.map((group) => (
                <ActivityTimelineDaySection
                    key={group.dateKey}
                    label={group.label}
                    items={group.items}
                    defaultOpen={group.defaultOpen ?? true}
                />
            ))}
        </div>
    );
}
